import {
  getCustomerVouchers,
  updateVoucherDetails,
  exportCustomerVouchers,
} from 'app/api/customer';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import { cancelCustomerVoucher } from 'app/api/voucher';
import { cancelPackage } from 'app/api/package';
import { isPackageRow } from 'app/components/PackageRow';
import { usePackageCollect } from 'app/components/PackageCollectPanel/usePackageCollect';
import { confirmAlert } from 'react-confirm-alert';
import { LocalSessionKey } from 'app/constants/app-constants';
import Message from 'app/utils/Message';
import { LocalStorage } from 'app/utils/storage';
import Toast from 'app/utils/Toast';
import { useCallback, useEffect, useRef, useState } from 'react';

const dataPerPage = 10;

const useCustomerVouchers = (latestDeletionDate, latestRestorationDate) => {
  const customerData = JSON.parse(
    LocalStorage.get(LocalSessionKey.CUSTOMER_DATA)
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [flatData, setFlatData] = useState([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPageCount, setFlatPageCount] = useState(1);

  const [beforeData, setBeforeData] = useState([]);
  const [beforeTotal, setBeforeTotal] = useState(0);
  const [beforePageCount, setBeforePageCount] = useState(1);

  const [afterData, setAfterData] = useState([]);
  const [afterTotal, setAfterTotal] = useState(0);
  const [afterPageCount, setAfterPageCount] = useState(1);

  const [values, setValues] = useState({
    phoneNumber: '',
    voucherCode: '',
    serialNumber: '',
    status: '',
  });

  const fetchSection = useCallback(
    async (page, bucket) => {
      setPageLoading(true);
      try {
        const response = await getCustomerVouchers(
          customerData?.id,
          page,
          dataPerPage,
          bucket,
          latestDeletionDate,
          latestRestorationDate
        );
        if (response?.responseCode === 200) {
          const rows = response?.data?.data || [];
          const count = response?.data?.count || 0;
          const pageCount = Math.ceil(count / dataPerPage) || 1;
          if (bucket === 'before') {
            setBeforeData(rows);
            setBeforeTotal(count);
            setBeforePageCount(pageCount);
          } else if (bucket === 'after') {
            setAfterData(rows);
            setAfterTotal(count);
            setAfterPageCount(pageCount);
          } else {
            setFlatData(rows);
            setFlatTotal(count);
            setFlatPageCount(pageCount);
          }
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [customerData?.id, latestDeletionDate, latestRestorationDate]
  );

  const fetchFlat = useCallback((page) => fetchSection(page, null), [fetchSection]);
  const fetchBefore = useCallback((page) => fetchSection(page, 'before'), [fetchSection]);
  const fetchAfter = useCallback((page) => fetchSection(page, 'after'), [fetchSection]);

  // Refresh every visible section after a mutation (collect/cancel) so the
  // user immediately sees the updated voucher state regardless of which
  // bucket the row falls into.
  const refreshAll = useCallback(() => {
    if (!latestDeletionDate) {
      fetchFlat(1);
      return;
    }
    fetchBefore(1);
    if (latestRestorationDate) fetchAfter(1);
  }, [latestDeletionDate, latestRestorationDate, fetchFlat, fetchBefore, fetchAfter]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // A successful package collect refreshes all three buckets; the panel closes itself.
  const packageCollect = usePackageCollect({ onSuccess: refreshAll });

  function handleNew(data) {
    // Branch on the explicit row type before reading any voucher-only field: a
    // package row has no voucherId, and purchase ids and reservation ids collide.
    if (isPackageRow(data)) {
      packageCollect.openFor(data, customerData?.phone);
      return;
    }
    setValues((prev) => ({
      ...prev,
      phoneNumber: customerData?.phone,
      voucherCode: '',
      voucherId: data?.voucherId || '',
      status: 3,
    }));
    setIsFilterOpen(true);
  }

  async function handleSubmit() {
    setPageLoading(true);
    try {
      const response = await updateVoucherDetails(values);
      if (response?.responseCode === 200) {
        Toast.success(response?.message);
        refreshAll();
      } else {
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
      }
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setIsFilterOpen(false);
      setPageLoading(false);
    }
  }

  async function cancelWholePackage(packageRow) {
    setPageLoading(true);
    try {
      const response = await cancelPackage(
        packageRow?.purchaseId,
        customerData?.id
      );
      if (response?.responseCode === 200) {
        Toast.success(response?.message);
        refreshAll();
      } else {
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
      }
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setPageLoading(false);
    }
  }

  async function handleDelete(_page, voucherdata) {
    // A package refunds its whole price in one credit, so it is confirmed once (D-15).
    // A single voucher cancel stays instant, exactly as before.
    if (isPackageRow(voucherdata)) {
      confirmAlert({
        title: '',
        message: Message.Package.CANCEL_CONFIRM,
        buttons: [
          {
            label: 'Yes',
            onClick: () => cancelWholePackage(voucherdata),
          },
          {
            label: 'No',
          },
        ],
        overlayClassName: 'overlay-custom-class-name',
      });
      return;
    }
    setPageLoading(true);
    try {
      const response = await cancelCustomerVoucher(
        voucherdata?.id,
        customerData?.id
      );
      if (response?.responseCode === 200) {
        Toast.success(response?.message);
        refreshAll();
      } else {
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
      }
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setPageLoading(false);
    }
  }

  // Async export of the customer's full voucher set. Returns a jobId, then
  // polls /export/status/:jobId and downloads when ready — identical flow to
  // the Bill History tab's export.
  //
  // The ref guard (not just the state flag) is what actually prevents a double
  // fire: setDownloadInProgress is async, so two fast clicks would both pass a
  // state-only check before the first re-render lands.
  const downloadInProgressRef = useRef(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const startDownload = useCallback(
    async (downloadType = 'excel') => {
      if (downloadInProgressRef.current) return;
      downloadInProgressRef.current = true;
      setDownloadInProgress(true);
      const finish = () => {
        downloadInProgressRef.current = false;
        setDownloadInProgress(false);
      };
      try {
        const response = await exportCustomerVouchers(
          customerData?.id,
          downloadType
        );
        const jobId = response?.data?.jobId;
        if (response?.responseCode !== 200 || !jobId) {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
          finish();
          return;
        }
        startExportPoll(jobId, {
          onDone: (url) => {
            triggerDownload(url);
            finish();
          },
          onFailed: () => {
            Toast.error(Message.Error.COMMON_MESSAGE);
            finish();
          },
          onError: () => {
            Toast.error(Message.Error.COMMON_MESSAGE);
            finish();
          },
        });
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
        finish();
      }
    },
    [customerData?.id]
  );

  return {
    pageLoading,

    startDownload,
    downloadInProgress,

    flatData,
    flatTotal,
    flatPageCount,
    fetchFlat,

    beforeData,
    beforeTotal,
    beforePageCount,
    fetchBefore,

    afterData,
    afterTotal,
    afterPageCount,
    fetchAfter,

    dataPerPage,

    handleNew,
    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    handleSubmit,
    handleDelete,
    packageCollect,
  };
};

export default useCustomerVouchers;
